import { useEffect, useState, useRef } from 'react';
import { Button } from './ui/button';
import { LogIn, LogOut } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

/**
 * Punch in / Punch out toggle.
 * - First click of the day → punch in (creates a new attendance session).
 * - Click while punched in → punch out (closes the session).
 * - A user can have multiple sessions per day; the button shows live HH:MM:SS
 *   while punched in, and the day's session count next to it.
 * Available to all roles. Surfaced on the Dashboard (alongside Lunch / Network).
 */
export const PunchButton = () => {
  const [state, setState] = useState({ is_punched_in: false, active_session_started_at: null, session_count: 0, total_minutes: 0 });
  const [displaySec, setDisplaySec] = useState(0);
  const [busy, setBusy] = useState(false);
  const tickRef = useRef(null);

  const hydrate = async () => {
    try {
      const res = await api.get('/attendance/me/today');
      setState(res.data);
    } catch { /* noop */ }
  };

  useEffect(() => { hydrate(); }, []);

  // Live timer for the current running session
  useEffect(() => {
    if (!state.is_punched_in || !state.active_session_started_at) {
      setDisplaySec(0);
      if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
      return;
    }
    const compute = () => {
      try {
        const started = new Date(state.active_session_started_at).getTime();
        setDisplaySec(Math.max(0, Math.floor((Date.now() - started) / 1000)));
      } catch { /* noop */ }
    };
    compute();
    tickRef.current = setInterval(compute, 1000);
    return () => { if (tickRef.current) clearInterval(tickRef.current); };
  }, [state.is_punched_in, state.active_session_started_at]);

  const fmt = (s) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const ss = s % 60;
    return h > 0
      ? `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(ss).padStart(2,'0')}`
      : `${String(m).padStart(2,'0')}:${String(ss).padStart(2,'0')}`;
  };

  const toggle = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await api.post('/attendance/toggle');
      setState(res.data);
      if (res.data.auto_closed_stale) {
        toast.info('Auto-closed a leftover session from a previous day before punching you in.');
      }
      if (res.data.state === 'punched_in') {
        toast.success(`Punched in at ${new Date(res.data.active_session_started_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`);
      } else {
        const mins = res.data.total_minutes || 0;
        const h = Math.floor(mins / 60); const m = mins % 60;
        toast.success(`Punched out — ${h ? `${h}h ` : ''}${m}m worked today (${res.data.session_count} session${res.data.session_count === 1 ? '' : 's'})`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to toggle attendance');
    } finally {
      setBusy(false);
    }
  };

  if (state.is_punched_in) {
    return (
      <Button
        size="default"
        onClick={toggle}
        disabled={busy}
        className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg ring-2 ring-emerald-300 gap-1.5 rounded-full px-4"
        data-testid="punch-btn-active"
        title="Punch out"
      >
        <LogOut className="h-4 w-4" />
        <span>Punched in</span>
        <span className="font-mono text-xs bg-white/20 px-1.5 py-0.5 rounded">{fmt(displaySec)}</span>
        {state.session_count > 1 && (
          <span className="text-[10px] bg-white/25 px-1.5 py-0.5 rounded-full font-semibold">#{state.session_count}</span>
        )}
      </Button>
    );
  }

  return (
    <Button
      size="default"
      onClick={toggle}
      disabled={busy}
      className="bg-emerald-500 hover:bg-emerald-600 text-white shadow-lg hover:shadow-xl ring-2 ring-emerald-200 gap-1.5 rounded-full px-4"
      data-testid="punch-btn"
      title="Punch in"
    >
      <LogIn className="h-4 w-4" />
      <span>Punch in</span>
      {state.session_count > 0 && (
        <span className="text-[10px] bg-white/25 px-1.5 py-0.5 rounded-full font-semibold" title="Sessions today">
          {state.session_count} done · {Math.floor(state.total_minutes / 60)}h {state.total_minutes % 60}m
        </span>
      )}
    </Button>
  );
};
